import { useEffect, useRef } from 'react';
import { useToast } from './toast/ToastContext';
import { connectivityToastFor } from '../utils/serviceWorker';

/**
 * 网络状态提示。
 *
 * 为什么需要：断网时最危险的失败模式是**用户以为保存成功了**。
 * `window.api` 的写入请求会失败并抛出，但用户可能没注意到；
 * 这里在离线时给出持续可见的提示，恢复后提示已恢复。
 *
 * 提示决策在纯函数 `connectivityToastFor` 里（便于单测），
 * 本组件只负责订阅事件与调用提示层。
 */
export function ConnectivityWatcher(): null {
  const { push } = useToast();
  const lastOnline = useRef<boolean | null>(null);

  useEffect(() => {
    const onConnectivity = (e: Event) => {
      const online = (e as CustomEvent<{ online: boolean }>).detail?.online ?? true;
      const toast = connectivityToastFor(lastOnline.current, online);
      lastOnline.current = online;
      if (toast) push({ kind: toast.kind, message: toast.message, duration: toast.duration });
    };

    window.addEventListener('xiaotianshu:connectivity', onConnectivity);
    return () => window.removeEventListener('xiaotianshu:connectivity', onConnectivity);
  }, [push]);

  // Service Worker 有新版本时提示（不自动刷新，避免打断写作）
  useEffect(() => {
    const onUpdateReady = () => {
      push({ kind: 'info', message: '应用有新版本，刷新页面即可更新', duration: 0 });
    };
    window.addEventListener('xiaotianshu:sw-update-ready', onUpdateReady);
    return () => window.removeEventListener('xiaotianshu:sw-update-ready', onUpdateReady);
  }, [push]);

  return null;
}
